/**
 * PipelineTab — Sync history, sync logs, sync status, and manual trigger controls.
 * Merges the former SyncHistoryTab and sync-related parts of ActivityTab.
 */

import React, { useState, useEffect, useCallback } from 'react';

import {
  RefreshCw, Play, CheckCircle, XCircle, AlertTriangle, ChevronDown, Loader2, X
} from 'lucide-react';
import type { SyncHistoryItem, SyncLogEntry, SyncStatusSource } from './types';
import { API_BASE, authFetch, formatRelativeTime } from './constants';
import ChangelogView from './ChangelogView';

interface PipelineTabProps {
  history: SyncHistoryItem[];
  selectedEnvironment: string;
  onRefreshHistory: () => void;
}

const PipelineTab: React.FC<PipelineTabProps> = ({ history, selectedEnvironment, onRefreshHistory }) => {
  const [pipelineMode, setPipelineMode] = useState<'history' | 'sync-logs' | 'sync-status' | 'changelog'>('history');
  const [syncLogs, setSyncLogs] = useState<SyncLogEntry[]>([]);
  const [syncStatus, setSyncStatus] = useState<Record<string, SyncStatusSource>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [triggerRunning, setTriggerRunning] = useState(false);
  const [triggerResult, setTriggerResult] = useState<any>(null);
  const [expandedLog, setExpandedLog] = useState<number | null>(null);

  // ---- Load Sync Logs ----
  const fetchSyncLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(
        `${API_BASE}/graph/sharepoint/sync-logs?days=14`
      );
      const data = await response.json();
      if (data.logs) {
        setSyncLogs(data.logs || []);
      } else {
        setError(data.error || 'Failed to load sync logs');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect');
    } finally {
      setLoading(false);
    }
  }, []);

  // ---- Load Sync Status ----
  const fetchSyncStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(
        `${API_BASE}/graph/sharepoint/sync-status?env=${selectedEnvironment}`
      );
      const data = await response.json();
      if (data.sources) {
        setSyncStatus(data.sources || {});
      } else {
        setError(data.error || 'Failed to load sync status');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect');
    } finally {
      setLoading(false);
    }
  }, [selectedEnvironment]);

  // ---- Trigger Sync ----
  const handleTriggerSync = async (full: boolean = false) => {
    if (triggerRunning) return;
    setTriggerRunning(true);
    setTriggerResult(null);
    try {
      const response = await authFetch(`${API_BASE}/graph/sharepoint/trigger-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ full, reindex: true, env: selectedEnvironment }),
      });
      const data = await response.json();
      setTriggerResult(data);
      if (data.success) {
        setTimeout(() => {
          onRefreshHistory();
          fetchSyncLogs();
          fetchSyncStatus();
        }, 2000);
      }
    } catch (err: any) {
      setTriggerResult({ success: false, error: err.message });
    } finally {
      setTriggerRunning(false);
    }
  };

  // Load data on mode change
  useEffect(() => {
    if (pipelineMode === 'sync-logs') fetchSyncLogs();
    else if (pipelineMode === 'sync-status') fetchSyncStatus();
  }, [pipelineMode, fetchSyncLogs, fetchSyncStatus]);

  return (
    <div className="history-tab">
      <div className="idx-tab-header">
        <h2>Pipeline</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Sync trigger buttons */}
          <button
            onClick={() => handleTriggerSync(false)}
            disabled={triggerRunning}
            style={{
              padding: '6px 14px', borderRadius: 6, border: 'none', cursor: triggerRunning ? 'not-allowed' : 'pointer',
              background: '#10b981', color: '#fff', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600,
              opacity: triggerRunning ? 0.6 : 1,
            }}
          >
            {triggerRunning ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            {triggerRunning ? 'Syncing...' : 'Trigger Sync'}
          </button>
          <button
            onClick={() => handleTriggerSync(true)}
            disabled={triggerRunning}
            style={{
              padding: '6px 14px', borderRadius: 6, border: '1px solid var(--idx-border-strong)', cursor: triggerRunning ? 'not-allowed' : 'pointer',
              background: 'var(--idx-bg-primary)', color: 'var(--idx-text-secondary)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500,
              opacity: triggerRunning ? 0.6 : 1,
            }}
          >
            <RefreshCw size={14} />
            Full Scan
          </button>
          {/* Mode toggles */}
          <div style={{ marginLeft: 12, display: 'flex', gap: 4 }}>
            {(['history', 'sync-logs', 'sync-status', 'changelog'] as const).map(mode => (
              <button
                key={mode}
                onClick={() => setPipelineMode(mode)}
                style={{
                  padding: '6px 14px', borderRadius: 6, border: '1px solid var(--idx-border-strong)', cursor: 'pointer',
                  background: pipelineMode === mode ? '#10b981' : 'var(--idx-bg-primary)',
                  color: pipelineMode === mode ? '#fff' : 'var(--idx-text-secondary)',
                  fontSize: 12, fontWeight: 500,
                }}
              >
                {mode === 'history' ? 'Sync Runs' : mode === 'sync-logs' ? 'Sync Logs' : mode === 'sync-status' ? 'Source Status' : 'Index Versions'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sync trigger result banner */}
      {triggerResult && (
        <div style={{
          padding: 12, marginBottom: 12, borderRadius: 8, fontSize: 13,
          background: triggerResult.success ? '#f0fdf4' : '#fef2f2',
          border: `1px solid ${triggerResult.success ? '#86efac' : '#fecaca'}`,
          color: triggerResult.success ? '#166534' : '#991b1b',
        }}>
          {triggerResult.success ? (
            <>
              <CheckCircle size={14} style={{ display: 'inline', marginRight: 6 }} />
              <strong>Sync completed.</strong>
              {triggerResult.syncResults?.sync_results?.map((r: any, i: number) => (
                <span key={i} style={{ marginLeft: 8, fontSize: 12, opacity: 0.85 }}>
                  {r.name}: +{r.files_added} ~{r.files_updated} -{r.files_deleted}
                </span>
              ))}
            </>
          ) : (
            <>
              <XCircle size={14} style={{ display: 'inline', marginRight: 6 }} />
              <strong>Sync failed:</strong> {triggerResult.error}
            </>
          )}
          <button
            onClick={() => setTriggerResult(null)}
            style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.5 }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Sync progress bar */}
      {triggerRunning && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 12, color: 'var(--idx-text-muted)' }}>
            <Loader2 size={14} className="animate-spin" style={{ color: '#10b981' }} />
            Syncing source sites to AskAldoKB...
          </div>
          <div style={{ height: 4, background: 'var(--idx-border)', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              height: '100%', background: 'linear-gradient(90deg, #10b981 0%, #34d399 50%, #10b981 100%)',
              backgroundSize: '200% 100%', animation: 'shimmer 1.5s linear infinite',
              width: '100%', borderRadius: 2,
            }} />
          </div>
          <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>
        </div>
      )}

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', fontSize: 13, marginBottom: 8 }}>
          <AlertTriangle size={14} style={{ display: 'inline', marginRight: 6 }} />
          {error}
        </div>
      )}

      {/* ================================================================= */}
      {/* Index History — per-index sync operations from Express API         */}
      {/* ================================================================= */}
      {pipelineMode === 'history' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button onClick={onRefreshHistory} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--idx-border-strong)', cursor: 'pointer', background: 'var(--idx-bg-primary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <RefreshCw size={12} /> Refresh
        </button>
      </div>
      
      {history.length === 0 ? (
        <div className="idx-empty-state">
          <p>No sync history available</p>
        </div>
      ) : (
        <table className="idx-history-table">
          <thead>
            <tr>
              <th>Index</th>
              <th>Mode</th>
              <th>Status</th>
              <th>Started</th>
              <th>Duration</th>
              <th>Added</th>
              <th>Updated</th>
              <th>Deleted</th>
              <th>Image Strategy</th>
              <th>Vision Cost</th>
              <th>Triggered By</th>
            </tr>
          </thead>
          <tbody>
            {history.map(item => {
              const duration = item.completedAt 
                ? Math.round((new Date(item.completedAt).getTime() - new Date(item.startedAt).getTime()) / 1000)
                : null;
              const visionMade = item.visionCallsMade ? parseInt(item.visionCallsMade, 10) : 0;
              const visionCapped = item.visionCallsCapped ? parseInt(item.visionCallsCapped, 10) : 0;
              const visionCached = item.visionCallsCached ? parseInt(item.visionCallsCached, 10) : 0;
              const estCost = item.estCostUsd ? parseFloat(item.estCostUsd) : 0;
              const hadVisionActivity = visionMade > 0 || visionCached > 0 || visionCapped > 0;

              return (
                <tr key={item.id} className={item.status}>
                  <td className="index-id">{item.indexId}</td>
                  <td style={{ textTransform: 'capitalize' }}>{item.mode}</td>
                  <td>
                    <span className={`status ${item.status}`}>
                      {item.status === 'running' && <Loader2 size={14} className="animate-spin" />}
                      {item.status === 'completed' && <CheckCircle size={14} />}
                      {item.status === 'failed' && <XCircle size={14} />}
                      {item.status}
                    </span>
                  </td>
                      <td>{new Date(item.startedAt).toLocaleString()}</td>
                      <td>{duration !== null ? `${duration}s` : '\u2014'}</td>
                  <td className="count added">+{item.documentsAdded}</td>
                      <td className="count updated">{'\u21BA'}{item.documentsUpdated}</td>
                  <td className="count deleted">-{item.documentsDeleted}</td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--idx-text-muted)' }}>
                    {item.imageMode || <span style={{ color: 'var(--idx-text-faint)' }}>default</span>}
                    {item.maxVisionCallsRequested ? (
                      <div style={{ fontSize: '0.7rem', color: 'var(--idx-text-faint)' }}>cap {item.maxVisionCallsRequested}</div>
                    ) : null}
                  </td>
                  <td style={{ fontSize: '0.78rem' }} title={hadVisionActivity ? `${visionMade} captioned, ${visionCached} cached, ${visionCapped} capped` : ''}>
                    {hadVisionActivity ? (
                      <>
                        <span style={{ fontWeight: 600, color: 'var(--idx-text-secondary)' }}>${estCost.toFixed(3)}</span>
                        {visionCapped > 0 && (
                          <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: '#fef3c7', color: '#92400e' }}>
                            {visionCapped} capped
                          </span>
                        )}
                      </>
                    ) : (
                      <span style={{ color: 'var(--idx-text-faint)' }}>{'\u2014'}</span>
                    )}
                  </td>
                  <td style={{ color: 'var(--idx-text-faint)', fontSize: '0.8rem' }}>{item.triggeredBy || 'System'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* Sync Logs — detailed sync operations from Function App blob logs  */}
      {/* ================================================================= */}
      {pipelineMode === 'sync-logs' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button onClick={fetchSyncLogs} disabled={loading} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--idx-border-strong)', cursor: 'pointer', background: 'var(--idx-bg-primary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <RefreshCw size={12} className={loading ? 'spinning' : ''} /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="idx-loading"><div className="idx-spinner" /><p>Loading sync logs...</p></div>
          ) : syncLogs.length === 0 ? (
            <div className="idx-empty-state">
              <p>No sync logs found</p>
              <p style={{ fontSize: 12, color: 'var(--idx-text-faint)' }}>Sync logs will appear here after the daily sync runs or a manual sync is triggered.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {syncLogs.map((log, i) => {
                const totalChanges = log.files_added + log.files_updated + log.files_deleted;
                const hasErrors = log.files_errored > 0 || (log.errors && log.errors.length > 0);
                const isExpanded = expandedLog === i;

                return (
                  <div key={i} style={{ border: '1px solid var(--idx-border)', borderRadius: 8, overflow: 'hidden', background: hasErrors ? '#fffbeb' : 'var(--idx-bg-primary)' }}>
                    {/* Summary row */}
                    <div
                      onClick={() => setExpandedLog(isExpanded ? null : i)}
                      style={{
                        display: 'grid', gridTemplateColumns: '140px 1fr 80px 80px 80px 80px 140px',
                        padding: '12px 16px', cursor: 'pointer', alignItems: 'center',
                        background: isExpanded ? 'var(--idx-bg-inset)' : 'transparent',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>
                          {log.started_at ? new Date(log.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Unknown'}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--idx-text-muted)' }}>
                          {log.started_at ? new Date(log.started_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''}
                        </div>
                      </div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{log.source_name || log.source_key}</div>
                      <div style={{ textAlign: 'center' }}>
                        <span style={{ color: '#16a34a', fontWeight: 600, fontSize: 14 }}>+{log.files_added}</span>
                        <div style={{ fontSize: 10, color: 'var(--idx-text-faint)' }}>added</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <span style={{ color: '#2563eb', fontWeight: 600, fontSize: 14 }}>~{log.files_updated}</span>
                        <div style={{ fontSize: 10, color: 'var(--idx-text-faint)' }}>updated</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <span style={{ color: '#dc2626', fontWeight: 600, fontSize: 14 }}>-{log.files_deleted}</span>
                        <div style={{ fontSize: 10, color: 'var(--idx-text-faint)' }}>deleted</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <span style={{ color: 'var(--idx-text-muted)', fontWeight: 500, fontSize: 14 }}>{log.files_unchanged}</span>
                        <div style={{ fontSize: 10, color: 'var(--idx-text-faint)' }}>unchanged</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
                        {totalChanges > 0 ? (
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#dcfce7', color: '#166534' }}>{totalChanges} changes</span>
                        ) : (
                          <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500, background: 'var(--idx-bg-tertiary)', color: 'var(--idx-text-muted)' }}>No changes</span>
                        )}
                        {hasErrors && <AlertTriangle size={14} style={{ color: '#d97706' }} />}
                        <ChevronDown size={14} style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: 'var(--idx-text-faint)' }} />
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--idx-border)', background: 'var(--idx-bg-secondary)', fontSize: 12 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                          <div><span style={{ fontWeight: 600, color: 'var(--idx-text-secondary)' }}>Started:</span> {log.started_at ? new Date(log.started_at).toLocaleString() : 'N/A'}</div>
                          <div><span style={{ fontWeight: 600, color: 'var(--idx-text-secondary)' }}>Completed:</span> {log.completed_at ? new Date(log.completed_at).toLocaleString() : 'N/A'}</div>
                          {log.completed_at && log.started_at && (
                            <div><span style={{ fontWeight: 600, color: 'var(--idx-text-secondary)' }}>Duration:</span> {Math.round((new Date(log.completed_at).getTime() - new Date(log.started_at).getTime()) / 1000)}s</div>
                          )}
                          <div><span style={{ fontWeight: 600, color: 'var(--idx-text-secondary)' }}>Errors:</span> {log.files_errored || 0}</div>
                        </div>
                        {log.changed_files && log.changed_files.length > 0 && (
                          <div style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--idx-text-secondary)' }}>Changed Files:</div>
                            <div style={{ maxHeight: 200, overflow: 'auto', background: 'var(--idx-bg-primary)', border: '1px solid var(--idx-border)', borderRadius: 6, padding: 8 }}>
                              {log.changed_files.map((file, fi) => (
                                <div key={fi} style={{ padding: '3px 0', borderBottom: fi < log.changed_files.length - 1 ? '1px solid var(--idx-bg-tertiary)' : 'none', fontSize: 12, fontFamily: 'monospace', color: 'var(--idx-text-mono)' }}>{file}</div>
                              ))}
                            </div>
                          </div>
                        )}
                        {log.errors && log.errors.length > 0 && (
                          <div>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: '#dc2626' }}>Errors:</div>
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: 8 }}>
                              {log.errors.map((err, ei) => (
                                <div key={ei} style={{ padding: '3px 0', color: '#991b1b', fontSize: 12 }}>{err}</div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* Sync Status — current state of each registered source              */}
      {/* ================================================================= */}
      {pipelineMode === 'sync-status' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button onClick={fetchSyncStatus} disabled={loading} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--idx-border-strong)', cursor: 'pointer', background: 'var(--idx-bg-primary)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4 }}>
              <RefreshCw size={12} className={loading ? 'spinning' : ''} /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="idx-loading"><div className="idx-spinner" /><p>Loading source status...</p></div>
          ) : Object.keys(syncStatus).length === 0 ? (
            <div className="idx-empty-state">
              <p>No index sources found</p>
              <p style={{ fontSize: 12, color: 'var(--idx-text-faint)' }}>Could not reach the function app or blob storage for this environment.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
              {Object.entries(syncStatus).map(([key, source]) => (
                <div key={key} style={{ border: '1px solid var(--idx-border)', borderRadius: 12, padding: 20, background: 'var(--idx-bg-primary)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: source.lastSync ? '#10b981' : 'var(--idx-border-strong)' }} />
                    <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{source.name || key}</h3>
                    {(source as any).indexStatus && (
                      <span style={{ marginLeft: 'auto', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 500, background: '#dbeafe', color: '#1e40af' }}>
                        {(source as any).indexStatus}
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--idx-text-muted)' }}>Last Sync</span>
                      <span style={{ fontWeight: 500 }}>{source.lastSync ? formatRelativeTime(source.lastSync) : 'Never'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--idx-text-muted)' }}>Last Full Scan</span>
                      <span style={{ fontWeight: 500 }}>{source.lastFullScan ? formatRelativeTime(source.lastFullScan) : 'Never'}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--idx-text-muted)' }}>Files Tracked</span>
                      <span style={{ fontWeight: 600, color: 'var(--idx-text-primary)' }}>{source.filesTracked?.toLocaleString() || 0}</span>
                    </div>
                    {(source as any).vectorCount != null && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--idx-text-muted)' }}>Vector Count</span>
                        <span style={{ fontWeight: 600, color: 'var(--idx-text-primary)' }}>{(source as any).vectorCount?.toLocaleString()}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--idx-text-muted)' }}>Total Syncs</span>
                      <span style={{ fontWeight: 500 }}>{source.syncCount || 0}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--idx-text-muted)' }}>Delta Token</span>
                      <span style={{
                        padding: '1px 6px', borderRadius: 10, fontSize: 11, fontWeight: 500,
                        background: source.hasDeltaToken ? '#dcfce7' : 'var(--idx-bg-tertiary)',
                        color: source.hasDeltaToken ? '#166534' : 'var(--idx-text-muted)',
                      }}>{source.hasDeltaToken ? 'Active' : 'None'}</span>
                    </div>
                    {(source as any).environment && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--idx-text-muted)' }}>Environment</span>
                        <span style={{ fontWeight: 500, textTransform: 'uppercase' }}>{(source as any).environment}</span>
                      </div>
                    )}
                    {(source as any).source && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: 'var(--idx-text-muted)' }}>Data Source</span>
                        <span style={{ fontWeight: 500, fontSize: 11, color: 'var(--idx-text-muted)' }}>{(source as any).source}</span>
                      </div>
                    )}
                  </div>
                  {source.error && (
                    <div style={{ marginTop: 12, padding: 8, background: '#fef2f2', borderRadius: 6, fontSize: 12, color: '#991b1b' }}>{source.error}</div>
                  )}
                  <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => handleTriggerSync(false)}
                      disabled={triggerRunning}
                      style={{
                        flex: 1, padding: '8px 12px', borderRadius: 6, border: 'none', cursor: 'pointer',
                        background: '#10b981', color: '#fff', fontSize: 12, fontWeight: 500,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      }}
                    ><RefreshCw size={12} /> Sync Now</button>
                    <button
                      onClick={() => handleTriggerSync(true)}
                      disabled={triggerRunning}
                      style={{
                        padding: '8px 12px', borderRadius: 6, border: '1px solid var(--idx-border-strong)', cursor: 'pointer',
                        background: 'var(--idx-bg-primary)', color: 'var(--idx-text-secondary)', fontSize: 12, fontWeight: 500,
                      }}
                    >Full</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ================================================================= */}
      {/* Changelog Mode                                                     */}
      {/* ================================================================= */}
      {pipelineMode === 'changelog' && (
        <ChangelogView selectedEnvironment={selectedEnvironment} />
      )}
    </div>
  );
};

export { PipelineTab };
